import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaHistory,
  FaBuilding,
  FaFlag,
  FaSpinner,
  FaExclamationTriangle,
  FaSync,
  FaCheckCircle,
  FaClock,
  FaBullhorn,
  FaUserCheck,
  FaCity,
  FaChartLine,
  FaNetworkWired
} from "react-icons/fa";

const LGADashboard = () => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (!refreshLoading) setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || "https://api.tinubu-ambode.org";
      const apiUrl = `${apiBaseUrl}/api/v1/analytics/dashboard`;
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      const authToken = token || localStorage.getItem('token') || localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || "API request failed");
      }
      
      setDashboardData(result.data);
      
    } catch (err) {
      console.error("Error fetching LGA dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshLoading(true);
    fetchDashboardData();
  };

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "15px",
  };

  const headerLeft = {
    flex: 1,
    minWidth: "250px",
  };

  const headerRight = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const refreshButton = {
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "10px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statTrend = {
    fontSize: "12px",
    color: "#22c55e",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  const statSubText = {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const performanceItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: "8px",
  };

  const performanceDetails = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const performanceName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const performanceMembers = {
    fontSize: "13px",
    color: "#3b82f6",
    textAlign: "right",
    fontWeight: "600",
  };

  const performanceChange = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "8px",
    borderTop: "1px dashed #e5e7eb",
  };

  const performanceChangeLabel = {
    fontSize: "12px",
    color: "#6b7280",
  };

  const activityItem = {
    display: "flex",
    gap: "12px",
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    alignItems: "flex-start",
  };

  const activityIcon = {
    width: "8px",
    height: "8px",
    backgroundColor: "#2563eb",
    borderRadius: "50%",
    marginTop: "8px",
    flexShrink: 0,
  };

  const activityDetails = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const activityTitle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    lineHeight: "1.4",
  };

  const activitySub = {
    fontSize: "13px",
    color: "#3b82f6",
    fontWeight: "500",
  };

  const activityTime = {
    fontSize: "12px",
    color: "#9ca3af",
    alignSelf: "flex-start",
    backgroundColor: "#f8fafc",
    padding: "4px 8px",
    borderRadius: "6px",
    marginTop: "4px",
  };

  const loadingContainer = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "16px",
    color: "#6b7280",
    gap: "15px",
  };

  const errorContainer = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "20px",
    textAlign: "center",
    gap: "15px",
  };

  const errorIcon = {
    fontSize: "48px",
    color: "#ef4444",
  };

  const errorText = {
    color: "#ef4444",
    fontSize: "16px",
    maxWidth: "500px",
    lineHeight: "1.5",
  };

  const retryButton = {
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const emptyState = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "200px",
    padding: "20px",
    textAlign: "center",
    gap: "15px",
    color: "#6b7280",
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    if (typeof num === 'number') {
      return num.toLocaleString();
    }
    return num.toString();
  };

  const formatPercentage = (num) => {
    if (num === undefined || num === null) return "0%";
    const value = typeof num === 'number' ? num : parseFloat(num);
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return "↗";
    if (trend < 0) return "↘";
    return "→";
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return "#22c55e";
    if (trend < 0) return "#ef4444";
    return "#6b7280";
  };

  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        min-height: 160px;
      }
      
      .dashboard-stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
      
      .dashboard-performance-item {
        flex-direction: row;
        align-items: center;
        gap: 0;
      }
      
      .dashboard-performance-details {
        width: auto;
        flex: 1;
      }
      
      .dashboard-performance-members {
        text-align: left;
        margin-top: 4px;
      }
      
      .dashboard-performance-change {
        width: auto;
        border-top: none;
        padding-top: 0;
        justify-content: flex-end;
        flex-direction: column;
        align-items: flex-end;
        min-width: 100px;
      }
      
      .dashboard-activity-details {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
      
      .dashboard-activity-time {
        align-self: center;
        margin-top: 0;
      }
      
      .dashboard-header {
        flex-direction: row;
        align-items: center;
      }
      
      .dashboard-header-right {
        margin-top: 0;
      }
    }
    
    @media (min-width: 1024px) {
      .dashboard-content-wrapper {
        padding: 30px 40px;
      }
      
      .dashboard-title {
        font-size: 24px;
      }
      
      .dashboard-subtitle {
        font-size: 15px;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-template-columns: 1fr;
        gap: 12px;
      }
      
      .dashboard-header {
        flex-direction: column;
        align-items: flex-start;
      }
      
      .dashboard-header-right {
        margin-top: 10px;
        width: 100%;
      }
      
      .dashboard-refresh-button {
        width: 100%;
        justify-content: center;
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-card {
        padding: 15px;
        min-height: 130px;
      }
      
      .dashboard-stat-value {
        font-size: 22px;
      }
    }
  `;

  if (loading) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={loadingContainer}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px' }} />
            <div>Loading LGA dashboard data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>Error: {error}</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
              disabled={refreshLoading}
            >
              <FaSync /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>No dashboard data available</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
              disabled={refreshLoading}
            >
              <FaSync /> Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { 
    summary, 
    wards_performance, 
    recent_activities, 
    lcdas_overview,
    geographic_distribution,
    engagement_metrics,
    metadata 
  } = dashboardData;

  const lgaName = metadata?.jurisdiction?.lga_name || 'Local Government';
  const stateName = metadata?.jurisdiction?.state || 'State';

  return (
    <>
      <style>{responsiveStyles}</style>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .dashboard-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
      `}</style>
      
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          {/* Header */}
          <div style={header} className="dashboard-header">
            <div style={headerLeft}>
              <h2 style={title} className="dashboard-title">
                Local Government Administration Dashboard
              </h2>
              <p style={subtitle} className="dashboard-subtitle">
                Welcome, {user?.full_name || 'LGA Administrator'}! Manage {lgaName}, {stateName} operations
              </p>
              {metadata?.generated_at && (
                <p style={{...subtitle, fontSize: '12px', marginTop: '5px'}}>
                  Last updated: {formatDate(metadata.generated_at)}
                </p>
              )}
            </div>
            {/* <div style={headerRight} className="dashboard-header-right">
              <button 
                style={{...refreshButton, 
                  opacity: refreshLoading ? 0.7 : 1,
                  backgroundColor: refreshLoading ? '#1d4ed8' : '#2563eb'
                }} 
                onClick={handleRefresh}
                disabled={refreshLoading}
                className="dashboard-refresh-button"
              >
                {refreshLoading ? (
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <FaSync />
                )}
                {refreshLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div> */}
          </div>

          {/* Stats Section */}
          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Total Members</span>
                <FaUsers color="#2563eb" />
              </div>
              <div style={statValue} className="dashboard-stat-value">
                {formatNumber(summary?.total_members)}
              </div>
              <div style={statSubText}>
                {formatNumber(summary?.verified_members)} verified
              </div>
              {summary?.member_growth !== undefined && (
                <div style={{...statTrend, color: getTrendColor(summary.member_growth)}}>
                  {getTrendIcon(summary.member_growth)} {summary.member_growth > 0 ? '+' : ''}
                  {formatPercentage(summary.member_growth)} growth
                </div>
              )}
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active LCDAs</span>
                <FaCity color="#8b5cf6" />
              </div>
              <div style={statValue} className="dashboard-stat-value">
                {formatNumber(lcdas_overview?.active_lcdas)}
              </div>
              <div style={statSubText}>
                {formatNumber(lcdas_overview?.total_lcdas)} total LCDAs
              </div>
              {lcdas_overview?.lcdas_growth !== undefined && (
                <div style={{...statTrend, color: getTrendColor(lcdas_overview.lcdas_growth)}}>
                  {getTrendIcon(lcdas_overview.lcdas_growth)} {lcdas_overview.lcdas_growth > 0 ? '+' : ''}
                  {formatNumber(lcdas_overview.lcdas_growth)} change
                </div>
              )}
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Wards</span>
                <FaNetworkWired color="#10b981" />
              </div>
              <div style={statValue} className="dashboard-stat-value">
                {formatNumber(summary?.active_wards)}
              </div>
              <div style={statSubText}>
                {formatNumber(summary?.total_wards)} total wards
              </div>
              {summary?.ward_growth !== undefined && (
                <div style={{...statTrend, color: getTrendColor(summary.ward_growth)}}>
                  {getTrendIcon(summary.ward_growth)} {summary.ward_growth > 0 ? '+' : ''}
                  {formatNumber(summary.ward_growth)} change
                </div>
              )}
            </div>
          </div>

          {/* Main Grid */}
          <div style={mainGrid} className="dashboard-main-grid">
            {/* Wards Performance */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartLine style={sectionIcon} />
                Wards Performance
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  Top Performing
                </span>
              </h3>

              {wards_performance?.length > 0 ? (
                wards_performance.slice(0, 5).map((ward, index) => (
                  <div key={index} style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>{ward.ward_name}</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(ward.total_members)} members
                      </div>
                    </div>
                    <div style={performanceChange} className="dashboard-performance-change">
                      <div style={{color: getTrendColor(ward.growth_percentage), fontWeight: "600", fontSize: "14px"}}>
                        {ward.growth_percentage > 0 ? '+' : ''}{formatPercentage(ward.growth_percentage)}
                      </div>
                      <div style={performanceChangeLabel}>vs last month</div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={emptyState}>
                  <FaExclamationTriangle size={24} />
                  <div>No ward performance data available</div>
                </div>
              )}
            </div>

            {/* Recent Activity Feed */}
            <div style={card} className="dashboard-card dashboard-activity-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaHistory style={sectionIcon} />
                Recent Activity
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  Latest Updates
                </span>
              </h3>

              {recent_activities?.length > 0 ? (
                recent_activities.slice(0, 5).map((activity, index) => (
                  <div key={index} style={activityItem} className="dashboard-activity-item">
                    <div style={activityIcon}></div>
                    <div style={activityDetails} className="dashboard-activity-details">
                      <div style={activityTitle}>{activity.title}</div>
                      <div style={activitySub}>{activity.subtitle}</div>
                      <div style={activityTime} className="dashboard-activity-time">
                        {formatDate(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={emptyState}>
                  <FaHistory size={24} />
                  <div>No recent activities</div>
                </div>
              )}
            </div>

            {/* LCDA Distribution */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaMapMarkerAlt style={sectionIcon} />
                LCDA Distribution
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  Members by LCDA
                </span>
              </h3>

              {geographic_distribution?.lcdas?.length > 0 ? (
                geographic_distribution.lcdas.map((lcdas, index) => (
                  <div key={index} style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>{lcdas.lcda_name}</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(lcdas.member_count)} members
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={emptyState}>
                  <FaMapMarkerAlt size={24} />
                  <div>No LCDA distribution data</div>
                </div>
              )}
            </div>

            {/* Engagement Metrics */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaBullhorn style={sectionIcon} />
                Engagement Metrics
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  This Month
                </span>
              </h3>

              {engagement_metrics ? (
                <>
                  <div style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>Active Members</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(engagement_metrics.active_members)}
                      </div>
                    </div>
                  </div>
                  <div style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>Group Chats</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(engagement_metrics.group_chats)}
                      </div>
                    </div>
                  </div>
                  <div style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>Announcements</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(engagement_metrics.announcements_sent)}
                      </div>
                    </div>
                  </div>
                  <div style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>Messages Sent</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatNumber(engagement_metrics.messages_sent)}
                      </div>
                    </div>
                  </div>
                  <div style={performanceItem} className="dashboard-performance-item">
                    <div style={performanceDetails} className="dashboard-performance-details">
                      <div style={performanceName}>Engagement Rate</div>
                      <div style={performanceMembers} className="dashboard-performance-members">
                        {formatPercentage(engagement_metrics.engagement_rate)}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div style={emptyState}>
                  <FaChartBar size={24} />
                  <div>No engagement data available</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LGADashboard;